import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import bankService from '../services/bankService';

type Bank = { code: string; name: string };

type BanksContextValue = {
  banks: Bank[];
  loading: boolean;
  error?: any;
  refresh: (useRemote?: boolean) => Promise<Bank[]>;
  findByCode: (code?: string | null) => Bank | null;
};

const BanksContext = createContext<BanksContextValue | undefined>(undefined);

export const BanksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);

  // Initial load: bank list is currently local to the frontend.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    bankService.list(false)
      .then((b) => { if (mounted) { setBanks(b || []); setError(null); } })
      .catch((err) => { if (mounted) { setBanks([]); setError(err); } })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const refresh = async (useRemote = false) => {
    setLoading(true);
    try {
      const b = await bankService.list(useRemote);
      setBanks(b || []);
      setError(null);
      return b || [];
    } catch (err) {
      setError(err);
      return banks;
    } finally {
      setLoading(false);
    }
  };

  const findByCode = (code?: string | null) => {
    if (!code) return null;
    const normalized = String(code).trim().padStart(3, '0');
    return banks.find(b => String(b.code) === normalized || String(b.code) === String(code)) || null;
  };

  return (
    <BanksContext.Provider value={{ banks, loading, error, refresh, findByCode }}>
      {children}
    </BanksContext.Provider>
  );
};

export const useBanks = (): BanksContextValue => {
  const ctx = useContext(BanksContext);
  if (!ctx) throw new Error('useBanks must be used within a BanksProvider');
  return ctx;
};

export default BanksContext;

